import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
    TextField,
    Modal,
    Box,
    CircularProgress,
    Select,
    MenuItem,
} from "@mui/material";
import cogoToast from "cogo-toast";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";
import TestimonialsCarousel from "../components/leadMagnet/TestimonialCarousal";
import Faq from "../components/Faq";
import bg0 from "../assets/images/leadMagnet/bg0.svg";
import bg1 from "../assets/images/leadMagnet/bg1.svg";
import bg2 from "../assets/images/leadMagnet/bg2.svg";
import blueBack from "../assets/images/leadMagnet/blue-back.png";
import {
    getLeadMagnet,
    postLeadMagnetResponseForm,
    downloadLeadMagnet,
} from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import { images } from "../constants/images";
import auditReportPdf from "../assets/audit_report_1.pdf";
import { Helmet } from "react-helmet-async";

// Modal styles
const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "500px" },
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    p: 4,
    outline: "none",
};

// Hero Section Component
const HeroSection = ({
    content,
    offeringType,
    formData,
    handleInputChange,
    showError,
    isSubmitting,
    submitLeadForm,
    downloadLink,
    handleManualDownload,
    isFormSubmitted,
    nicheTypes = [], // Add default value
}) => (
    <section
        className="page-hero-sec"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            minHeight: "80vh",
            display: "flex",
            alignItems: "center",
        }}
    >
        <div className="container mb-5">
            <div className="row align-items-center">
                <div className="col-lg-7 col-md-12">
                    <div className="hero-content text-white">
                        <h1
                            className="head-font mb-4"
                            style={{
                                fontSize: "3.5rem",
                                fontWeight: "bold",
                                lineHeight: "1.2",
                            }}
                        >
                            Finally, an ERP Built Only for Manufacturers,  Not for “Every Business”
                        </h1>
                        <p
                            className="fs-17 mb-4 text-warning"
                            style={{ lineHeight: "1.6", fontWeight: "bold" }}
                        >
                            While others try to fit your factory into a generic template, our ERP is built around how you actually work,  on the shop floor, in real time.

                        </p>
                        <br />
                        <p
                            className="fs-17 mb-4 text-warning"
                            style={{ lineHeight: "1.6", fontWeight: "bold" }}
                        >
                            No retail clutter. No unnecessary modules. Just pure manufacturing intelligence.
                        </p>
                    </div>
                </div>
                <div className="col-lg-5 col-md-12">
                    <div
                        className="hero-form bg-white p-4 rounded shadow-lg"
                        style={{ maxWidth: "400px", margin: "0 auto" }}
                    >
                        <div className="text-center mb-4">
                            <h5 className="tc-primary head-font mb-2">
                                Our live demo is in development. Get early access and experience how an ERP built only for manufacturers actually feels.
                            </h5>
                            <p className="text-muted small">
                                Fill in your details and get insights instantly,  no obligations.
                            </p>
                        </div>


                        {/* Success message for Manufacturing ERP */}
                        {isFormSubmitted && offeringType === "Manufacturing ERP" && (
                            <div className="alert alert-success text-center mb-4">
                                <h6 className="text-success mb-2">
                                    ✅ Manufacturing ERP Registration Successful!
                                </h6>
                                <p className="mb-0 small">
                                    Thank you for registering! Check your email for Manufacturing ERP
                                    details and joining instructions.
                                </p>
                            </div>
                        )}

                        {/* Show form only if not submitted or if E-Book and no download link yet */}
                        {(!isFormSubmitted ||
                            (offeringType === "Manufacturing ERP")) && (
                                <>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            error={showError && !formData.name.trim()}
                                            helperText={
                                                showError && !formData.name.trim()
                                                    ? "Name is required"
                                                    : ""
                                            }
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Company Name"
                                            name="company_name"
                                            type="text"
                                            value={formData.company_name}
                                            onChange={handleInputChange}
                                            error={showError && !formData.company_name.trim()}
                                            helperText={
                                                showError && !formData.company_name.trim()
                                                    ? "Valid company name is required"
                                                    : ""
                                            }
                                            margin="dense"
                                            sx={{ "& input": { background: "#f8fafc" } }}
                                            inputProps={{ maxLength: 10 }}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            error={showError && !formData.email.trim()}
                                            helperText={
                                                showError && !formData.email.trim()
                                                    ? "Valid email is required"
                                                    : ""
                                            }
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            name="phone"
                                            type="text"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            error={showError && !formData.phone.trim()}
                                            helperText={
                                                showError && !formData.phone.trim()
                                                    ? "Valid phone is required"
                                                    : ""
                                            }
                                            inputProps={{ maxLength: 10 }}
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>

                                    

                                    {/* <div className="mb-4">
                    <Select
                      fullWidth
                      name="nicheType"
                      value={formData.nicheType}
                      onChange={handleInputChange}
                      displayEmpty
                      error={showError && !formData.nicheType}
                      sx={{
                        "& .MuiSelect-select": {
                          background: "#e0e5ef",
                          padding: "12px 14px",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#e0e5ef",
                          },
                          "&:hover fieldset": {
                            borderColor: "#b0b5bf",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3f51b5",
                          },
                        },
                      }}
                      inputProps={{
                        "aria-label": "Industry Type",
                      }}
                    >
                      <MenuItem value="" disabled>
                        Industry Type
                      </MenuItem>
                      {nicheTypes?.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>

                    {showError && !formData.nicheType && (
                      <p
                        style={{
                          color: "#d32f2f",
                          fontSize: "0.75rem",
                          margin: "3px 14px 0",
                        }}
                      >
                        Industry type is required
                      </p>
                    )}
                  </div>

                  {formData.nicheType === "Other" && (
                    <div className="mb-4">
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Specify Your Industry"
                        name="industry_other"
                        value={formData.industry_other}
                        onChange={handleInputChange}
                        error={
                          showError &&
                          formData.nicheType === "Other" &&
                          !formData.industry_other.trim()
                        }
                        helperText={
                          showError &&
                            formData.nicheType === "Other" &&
                            !formData.industry_other.trim()
                            ? "Please specify your industry"
                            : ""
                        }
                        required={formData.nicheType === "Other"}
                        margin="normal"
                        inputProps={{ maxLength: 100 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& input": {
                              background: "#e0e5ef",
                              padding: "12px 14px",
                            },
                          },
                        }}
                      />
                    </div>
                  )} */}

                                    <button
                                        onClick={submitLeadForm}
                                        className="LM_link_btn1"
                                        disabled={isSubmitting}
                                        // style={{
                                        //     background: "#0a2458",
                                        //     border: "none",
                                        //     padding: "12px",
                                        //     fontWeight: "600",
                                        // }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <CircularProgress
                                                    size={20}
                                                    sx={{ color: "white", mr: 1 }}
                                                />
                                                Submitting...
                                            </>
                                        ) : (
                                            "Get Early Access"
                                        )}
                                    </button>

                                    <p className="text-center text-muted small mt-2 mb-0">
                                        No spam. Only updates and early access invitations.
                                    </p>
                                </>
                            )}
                    </div>
                </div>
            </div>
        </div>
    </section>
);

// Understanding Section Component
const DeserveClaritySection = ({
    content,
    isLoading,
    openModal,
    description,
}) => (
    <section
        className="py-4 d-flex align-items-center justify-content-center min-vauto"
        style={{
            backgroundImage: `url(${bg0})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container mt-5">
            <div className="row justify-content-center mt-5">
                <div className="col-lg-8">
                    {/* <div
            className="position-relative mb-4 rounded bg-transparent overflow-hidden"
            style={{ paddingBottom: "56.25%" }}
          >
            {isLoading ? (
              <div className="position-absolute top-50 start-50 translate-middle">
                <CircularProgress />
              </div>
            ) : (
              <img
                src={images.adminSystems}
                alt="Banner"
                className="position-absolute top-0 start-0 w-100 auto"
                style={{ objectFit: "cover" }}
              />
            )}
          </div> */}
                    <br />

                    {/* <div className="container"> */}
                    <h3 className="tc-primary head-font mb-4 text-center">
                        Why Off-the-Shelf ERPs Fail Manufacturers ?
                    </h3>

                     <p className="fs-20 text-muted mb-1 text-center">
                            Manufacturers are stuck with software that doesn’t match how they work. Here’s why it hurts your factory:
                        </p>

                    <div className="text-center text-black">
                        <p
                            className="mt-5"
                            style={{
                                fontFamily: "Inter",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "24px",
                                lineHeight: "36px",
                                color: "#000000",
                            }}
                        >
                            <ul
                                className="text-black"
                                style={{
                                    listStyle: "none",
                                    fontFamily: "Inter",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    fontSize: "24px",
                                    lineHeight: "36px",
                                    color: "#000000",
                                }}
                            >
                                <li style={{ fontSize: 20 }}>
                                    👉 Rigid workflows that force your team to adjust to the software.
                                </li>
                                <li style={{ fontSize: 20 }}>
                                    👉 Bloated modules for HR, CRM, or retail,  which you never use.

                                </li>
                                <li style={{ fontSize: 20 }}>
                                    👉Poor inventory tracking for raw materials like glass, chemicals, or perishables.

                                </li>
                                <li style={{ fontSize: 20 }}>
                                    👉 No real-time visibility into production or downtime.

                                </li>
                                <li style={{ fontSize: 20 }}>
                                    👉 Vendor dependency,  every tweak or report request becomes a ticket.

                                </li>
                                <li style={{ fontSize: 20 }}>
                                    👉 Per-user licensing traps that explode your cost as you scale.

                                </li>
                            </ul>
                            <br />
                        </p>
                    </div>

                    <div dangerouslySetInnerHTML={{ __html: description }} />
                    <div className="text-center">
                        <button onClick={openModal} className="LM_link_btn">
                           Get Early Access
                        </button>
                    </div>
                    {/* </div> */}
                </div>
            </div>
        </div>
    </section>
);

const ThingsYouWillGetInThisReport = ({ openModal }) => {
    return (
        <section
            className="d-flex align-items-center justify-content-center min-vauto"
            style={{
                backgroundImage: `url(${bg0})`,
                backgroundPosition: "center",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                minHeight: "100vh", // ensures full vertical height
            }}
        >
            <div className="container mt-1">
                <div className="row justify-content-center align-items-center mt-5" style={{ minHeight: "80vh" }}>
                    {/* Left Side */}
                    <div className="col-lg-6 d-flex flex-column align-items-center justify-content-center text-center">
                        <h2 className="tc-primary text-center head-font mb-4">
                            The Manufacturing-First ERP
                        </h2>
                        <p className="fs-20 text-muted mb-1 text-center">
                            A purpose-built system designed only for factories.
                            No bloat, no compromises, and no shared servers.
                        </p>

                        <ul
                            className="text-center"
                            style={{
                                listStyle: "none",
                                fontFamily: "Inter",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "20px",
                                lineHeight: "32px",
                                color: "#000000",
                                paddingLeft: 0,
                            }}
                        >
                            <li>✅ Built for Manufacturing: All modules revolve around production, inventory, and quality.</li>
                            <li>✅ You Own Your Data: Each client gets their own deployed copy.</li>
                            <li>✅ Customizable When You Need It: Add up to 4 custom modules @ $4,000/year each.</li>
                            <li>✅ Scalable: From one production line to ten, grows seamlessly.</li>
                            <li>✅ Secure & Fast: No data sharing, no lag, no vendor lock-ins.</li>
                        </ul>

                        <div className="mt-4">
                            <button onClick={openModal} className="LM_link_btn">
                                Get Early Access
                            </button>
                        </div>
                    </div>

                    {/* Right Side */}
                    <div className="col-lg-6 text-center d-flex align-items-center justify-content-center">
                        <img
                            src={images.topimage1}
                            alt="Banner"
                            className="w-100 h-85 mb-3"
                            style={{ objectFit: "cover" }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};


// const ThingsYouWillGetInThisReport = ({ openModal }) => {

//     return (
//         <section
//             className="d-flex align-items-center justify-content-center min-vauto"
//             style={{
//                 backgroundImage: `url(${bg0})`,
//                 backgroundPosition: "center",
//                 backgroundSize: "cover",
//                 backgroundRepeat: "no-repeat",
//                  minHeight: "100vh", 
//             }}
//         >
//             <div className="container mt-1">
//                 <div className="row justify-content-center mt-5">
//                     {/* Left Side */}
//                     <div className="col-lg-6 d-flex flex-column align-items-center text-center">
//                         <h2 className="tc-primary text-center head-font mb-4">
//                             The Manufacturing-First ERP
//                         </h2>
//                         <p className="fs-20 text-muted mb-1 text-center">
//                             A purpose-built system designed only for factories.
//                             No bloat, no compromises, and no shared servers.
//                         </p>

//                         <ul
//                             className="text-center"
//                             style={{
//                                 listStyle: "none",
//                                 fontFamily: "Inter",
//                                 fontStyle: "normal",
//                                 fontWeight: 400,
//                                 fontSize: "20px",
//                                 lineHeight: "32px",
//                                 color: "#000000",
//                             }}
//                         >
//                             <li>✅ Built for Manufacturing: All modules revolve around production, inventory, and quality.</li>
//                             <li>✅ You Own Your Data: Each client gets their own deployed copy.</li>
//                             <li>
//                                 ✅ Customizable When You Need It: Add up to 4 custom modules @ $4,000/year each.

//                             </li>
//                             <li>
//                                 ✅ Scalable: From one production line to ten,  grows seamlessly.

//                             </li>
//                             <li>
//                                 ✅ Secure & Fast: No data sharing, no lag, no vendor lock-ins.

//                             </li>

//                         </ul>

                    
//                         <div className="mt-4">
//                             <button onClick={openModal} className="LM_link_btn">
//                                 Get Early Access
//                             </button>
//                         </div>
//                     </div>

//                     {/* Right Side */}
//                     <div className="col-lg-6 text-center">
//                         <img
//                             src={images.topimage1}
//                             alt="Banner"
//                             className="top-0 start-0 w-100 h-85 mb-3"
//                             style={{ objectFit: "cover" }}
//                         />
//                     </div>


//                 </div>
//             </div>
//         </section>
//     );
// };

const WhoIsThisFor = ({ openModal }) => (
    <section
        className="d-flex align-items-center justify-content-center min-vauto"
        style={{
            backgroundImage: `url(${bg1})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container mt-1 mb-5">
            <div className="row justify-content-center mt-5">
                <div className="col-lg-6">
                    <img
                        src={images.whoIsThisPerfectFor}
                        alt="Banner"
                        className="top-0 start-0 w-100 auto"
                        style={{ objectFit: "cover" }}
                    />
                </div>
                <div className="col-lg-6 align-self-center">
                    <h2 className="tc-primary text-center head-font mb-4">
                        Who This Is Perfect For?
                    </h2>
                    <p className="fs-20 text-muted mb-1 text-center">
                        Non‑technical founders and operators who want clarity before code.
                    </p>
                    <p
                        className="py-5 text-center"
                        style={{
                            fontFamily: "Inter",
                            fontStyle: "normal",
                            fontWeight: 400,
                            fontSize: "24px",
                            lineHeight: "36px",
                            color: "#000000",
                        }}
                    >
                        <ul
                            className="text-center"
                            style={{
                                listStyle: "none",
                                fontFamily: "Inter",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "24px",
                                lineHeight: "36px",
                                color: "#000000",
                            }}
                        >
                            <li style={{ fontSize: 20 }}>
                                👉 "I feel lost—what do I even ask a developer?"
                            </li>
                            <li style={{ fontSize: 20 }}>
                                👉 "I'm scared of getting overcharged or locked into bad tech."
                            </li>
                            <li style={{ fontSize: 20 }}>
                                👉 "I want a simple plan with costs, timelines, and priorities."
                            </li>
                        </ul>
                        <br />
                        <center style={{ textAlign: "center" }}>
                            If that's you, this will save weeks and lots of money.
                        </center>
                    </p>
                    <br />
                    <div className="text-center">
                        <button onClick={openModal} className="LM_link_btn">
                            Get Early Access
                        </button>
                    </div>
                    <br /> <br />
                </div>
            </div>
        </div>
    </section>
);

// Key Points Section Component
const JustWantRightSoftwareSection = ({ description, openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <h3 className="tc-primary head-font mb-4 text-center text-white">
                Our Promise to You
            </h3>
            <p className="fs-20 mb-4 text-center text-white">
                We’re not selling ERP software,  we’re giving manufacturers back control, flexibility, 
                <br/>
                and peace of mind.No data sharing. No downtime. No nonsense
            </p>

            <div className="text-center text-white">
                <p
                    className="mt-5"
                    style={{
                        fontFamily: "Inter",
                        fontStyle: "normal",
                        fontWeight: 400,
                        fontSize: "24px",
                        lineHeight: "36px",
                        color: "#000000",
                    }}
                >
                    <ul
                        className="text-white"
                        style={{
                            listStyle: "none",
                            fontFamily: "Inter",
                            fontStyle: "normal",
                            fontWeight: 400,
                            fontSize: "24px",
                            lineHeight: "36px",
                            color: "#000000",
                        }}
                    >
                        <li style={{ fontSize: 20 }}>
                            👉 100% Data Ownership
                        </li>
                        <li style={{ fontSize: 20 }}>
                            👉 Process Flexibility
                        </li>
                        <li style={{ fontSize: 20 }}>
                            👉 Real-time Visibility
                        </li>

                    </ul>
                    <br />
                </p>
            </div>
         

            <div className="text-center">
                <button onClick={openModal} className="LM_link_btn_white w-auto">
                    Get Early Access
                </button>
            </div>
        </div>
    </section>
);

const HowItWorks = ({ openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <h3 className="tc-primary head-font mb-4 text-center text-white">
                How It Works (Simple)?
            </h3>
            <p className="fs-20 mb-4 text-center text-white">
                Three steps. No jargon. No pressure.
            </p>
            <div className="text-center text-white">
                <p
                    className="mt-5"
                    style={{
                        fontFamily: "Inter",
                        fontStyle: "normal",
                        fontWeight: 400,
                        fontSize: "24px",
                        lineHeight: "36px",
                        color: "#000000",
                    }}
                >
                    <ul
                        className="text-white"
                        style={{
                            listStyle: "none",
                            fontFamily: "Inter",
                            fontStyle: "normal",
                            fontWeight: 400,
                            fontSize: "24px",
                            lineHeight: "36px",
                            color: "#000000",
                        }}
                    >
                        <li style={{ fontSize: 20 }}>
                            👉 Answer 10-12 quick questions about your idea and goals
                        </li>
                        <li style={{ fontSize: 20 }}>
                            👉 We analyze & model MVP features, tech choices, integrations
                        </li>
                        <li style={{ fontSize: 20 }}>
                            👉 Get your report + optional free 1:1 clarity call
                        </li>
                    </ul>
                    <br />
                </p>
            </div>
            <div className="text-center text-white mb-4">
                <h5 className="fw-bold text-white fs-22">
                    Five minutes now saves months later—truly.
                </h5>
            </div>

            <div className="text-center">
                <button onClick={openModal} className="LM_link_btn_white w-auto">
                    Get My Free Audit Report
                </button>
            </div>
        </div>
    </section>
);

// Consequences Section Component
const ReportParts = ({
    badEffects = [],
    helpOffer,
    offeringType,
    openModal,
}) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `url(${bg1})`,
            backgroundPosition: "center",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
        }}
    >
        <div className="container text-center">
            <h2 className="tc-primary text-center head-font mb-4">
                Core Modules That Matter
            </h2>
            <p className="fs-20 text-muted mb-4">
                Everything your factory needs,  nothing you don’t.
            </p>
            <div className="row text-center mb-5 justify-content-center">
                {badEffects.map((effect) => (
                    <div key={effect.id} className="col-lg-4 mb-4">
                        <div className="border  bg-white rounded p-4 auto d-flex flex-column justify-content-center align-items-center">
                            <img
                                src={effect.icon}
                                alt="Icon"
                                style={{ width: "100px", height: "100px" }}
                            />
                            <br />
                            <h3
                                className="mt-3 mb-0"
                                dangerouslySetInnerHTML={{ __html: effect.title }}
                                style={{ color: "#0b1d51" }}
                            />
                            <p
                                className="mt-3 mb-0"
                                dangerouslySetInnerHTML={{ __html: effect.text }}
                            ></p>
                        </div>
                    </div>
                ))}
            </div>

            <button onClick={openModal} className="LM_link_btn">
                Get Early Access
            </button>
        </div>
    </section>
);



// const WhatYouWillGet = ({
//     badEffects = [],
//     helpOffer,
//     offeringType,
//     openModal,
// }) => (
//     <section
//         className="py-4 mb-5"
//         style={{
//             backgroundImage: `url(${bg1})`,
//             backgroundPosition: "center",
//             backgroundSize: "contain",
//             backgroundRepeat: "no-repeat",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             minHeight: "100vh",
//         }}
//     >
//         <div className="container text-center">
//             <h2 className="text-center tc-primary head-font" style={{ fontSize: "28px" }}>
//                 Why You Really Need This Report?
//             </h2>
//             <p className="fs-20 text-muted mb-4">
//                 If you're not from a tech background, you're already at a disadvantage.
//                 Most business owners get trapped between:{" "}
//                 <b>
//                     Sweet-talking agencies, Vague quotes with no clarity & Shiny promises,
//                     but zero accountability
//                 </b>
//                 .
//             </p>
//             <div className="row text-center mb-5 justify-content-center">
//                 {badEffects.map((effect) => (
//                     <div key={effect.id} className="col-lg-3 mb-4">
//                         <div className="border  bg-white rounded p-4 auto d-flex flex-column justify-content-center align-items-center">
//                             <img
//                                 src={effect.icon}
//                                 alt="Icon"
//                                 style={{ width: "100px", height: "100px" }}
//                             />
//                             <br />
//                             <h3
//                                 className="mt-3 mb-0"
//                                 dangerouslySetInnerHTML={{ __html: effect.title }}
//                                 style={{ color: "#0b1d51" }}
//                             />

//                             <p
//                                 className="mt-3 mb-0"
//                                 dangerouslySetInnerHTML={{ __html: effect.text }}
//                             ></p>
//                         </div>
//                     </div>
//                 ))}
//             </div>

//             <button onClick={openModal} className="LM_link_btn">
//                 Get My Free Audit Report
//             </button>
//         </div>
//     </section>
// );

// Testimonials Section Component
const TestimonialsSection = ({ reviews = [], offeringType }) => (
    <section
        className="py-5 "
        style={{
            backgroundImage: `url(${bg2})`,
            backgroundPosition: "",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            display: "flex",
            alignItems: "",
            justifyContent: "center",
        }}
    >
        <div className="container">
            <div className="text-center mb-5">
                <h2 className="tc-primary head-font">Real Stories, Real Results </h2>
                <p className="fs-20 text-muted mb-4">
                    When you're building software without tech knowledge, it's easy to
                    feel lost… and worse,  taken for a ride.
                </p>
            </div>
            <TestimonialsCarousel
                testimonials={reviews.map((review) => ({
                    id: review.id,
                    name: review.name,
                    role: review.designation,
                    content: review.content,
                    avatar: review.image,
                }))}
            />
        </div>
    </section>
);

const MiniStory = ({ openModal }) => (
    <section
        className="pt-5 "
        style={{
            backgroundImage: `url(${bg2})`,
            backgroundPosition: "",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            display: "flex",
            alignItems: "",
            justifyContent: "center",
        }}
    >
        <div className="container">
            <div className="text-center mb-5">
                <h2 className="tc-primary head-font">Mini Story</h2>
                <p className="fs-20 text-muted mb-4">
                    <br />
                    Ravi, a first-time founder, wanted to build a custom logistics app. He
                    found an agency online, got a vague 3-page quote with no tech details,
                    and jumped in.
                    <br />
                    <br />
                    Six months later, the app was buggy, painfully slow, and falling
                    apart. Turns out, he never knew what tech they used or what phases
                    were included.
                    <br />
                    <br />
                    By the time he came to us, he had already burned through ₹8 lakhs.
                    <br />
                    <br />
                    In just 2 weeks, we gave him a clear plan, the right tech stack, and a
                    roadmap that saved him another ₹5 lakhs, and most importantly, gave
                    him back his peace of mind.
                    <br />
                    <br />
                    That’s the power of knowing before you build.
                </p>
                <br />
                <br />
                <button onClick={openModal} className="LM_link_btn">
                    Get My Free Audit Report
                </button>
            </div>
        </div>
    </section>
);

// Final CTA Section Component
const FinalCtaSection = ({ offeringType, openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <div className="text-center text-white">
                <h2 className="tc-primary head-font mb-4 text-center text-white">
                     Be Part of the Early Access Program.
                </h2>
                <p className="fs-20 mb-4 text-center text-white">
                    Our live demo version is currently in development.
                    <br />
                    Get notified the moment it’s ready,  and be among 
                    
                    <br />
                    the first to explore how manufacturing ERP should feel.
                    
                </p>

                <div className="text-center">
                    <button onClick={openModal} className="LM_link_btn_white">
                       Get Early Access
                    </button>
                </div>
            </div>
        </div>
    </section>
);

// Lead Capture Modal Component
const LeadCaptureModal = ({
    isOpen,
    closeModal,
    formData,
    handleInputChange,
    showError,
    isSubmitting,
    submitLeadForm,
    offeringType,
    downloadLink,
    handleManualDownload,
    isFormSubmitted,
    nicheTypes = [],
}) => (
    <Modal
        open={isOpen}
        onClose={closeModal}
        aria-labelledby="lead-capture-modal"
        aria-describedby="lead-capture-form"
    >
        <Box sx={modalStyle}>
            <div className="text-center mb-4">
                <h4 className="tc-primary head-font">
                    {offeringType === "E-Book"
                        ? "Get Your Free E-Book"
                        : "Our live demo is in development. Get early access and experience how an ERP built only for manufacturers actually feels."}
                </h4>
                <p className="text-muted">
                    {offeringType === "E-Book"
                        ? "Enter your details below and we'll send the PDF guide directly to your inbox."
                        : "Enter your details below to reserve your spot in our exclusive Manufacturing ERP."}
                </p>
            </div>


            {isFormSubmitted && offeringType === "Manufacturing ERP" && (
                <div className="alert alert-success text-center mb-4">
                    <h6 className="text-success mb-2">
                        ✅ Manufacturing ERP Registration Successful!
                    </h6>
                    <p className="mb-0 small">
                        Thank you for registering! Check your email for Manufacturing ERP
                        details and joining instructions.
                    </p>
                </div>
            )}

            {(!isFormSubmitted || (offeringType === "Manufacturing ERP")) && (
                <>
                    <div className="mb-3">
                        <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={showError && !formData.name.trim()}
                            helperText={
                                showError && !formData.name.trim() ? "Name is required" : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 100 }}
                        />
                    </div>

                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Company Name"
                            name="company_name"
                            type="text"
                            value={formData.company_name}
                            onChange={handleInputChange}
                            error={showError && !formData.company_name.trim()}
                            helperText={
                                showError && !formData.company_name.trim()
                                    ? "Valid company name is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 10 }}
                        />
                    </div>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={showError && !formData.email.trim()}
                            helperText={
                                showError && !formData.email.trim()
                                    ? "Valid email is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 150 }}
                        />
                    </div>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            type="text"
                            value={formData.phone}
                            onChange={handleInputChange}
                            error={showError && !formData.phone.trim()}
                            helperText={
                                showError && !formData.phone.trim()
                                    ? "Valid phone is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 10 }}
                        />
                    </div>

                    

                  

                    {/* <div className="mb-4">
            <Select
              fullWidth
              name="nicheType"
              value={formData.nicheType}
              onChange={handleInputChange}
              displayEmpty
              error={showError && !formData.nicheType}
              sx={{
                "& .MuiSelect-select": {
                  background: "#e0e5ef",
                  padding: "12px 14px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#e0e5ef",
                  },
                  "&:hover fieldset": {
                    borderColor: "#b0b5bf",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3f51b5",
                  },
                },
              }}
              inputProps={{
                "aria-label": "Industry Type",
              }}
            >
              <MenuItem value="" disabled>
                Industry Type
              </MenuItem>
              {nicheTypes?.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>

            {showError && !formData.nicheType && (
              <p
                style={{
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  margin: "3px 14px 0",
                }}
              >
                Industry type is required
              </p>
            )}
          </div>

          {formData.nicheType === "Other" && (
            <div className="mb-4">
              <TextField
                fullWidth
                variant="outlined"
                label="Specify Your Industry"
                name="industry_other"
                value={formData.industry_other}
                onChange={handleInputChange}
                error={
                  showError &&
                  formData.nicheType === "Other" &&
                  !formData.industry_other.trim()
                }
                helperText={
                  showError &&
                    formData.nicheType === "Other" &&
                    !formData.industry_other.trim()
                    ? "Please specify your industry"
                    : ""
                }
                required={formData.nicheType === "Other"}
                margin="normal"
                inputProps={{ maxLength: 100 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& input": {
                      background: "#e0e5ef",
                      padding: "12px 14px",
                    },
                  },
                }}
              />
            </div>
          )} */}
                    <div className="d-flex gap-2">
                        <button
                            onClick={closeModal}
                            className="btn btn-outline-secondary flex-fill"
                            
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={submitLeadForm}
                            className="LM_link_btn1"
                            disabled={isSubmitting}
                            // style={{
                            //     background: "#1e3a8a",
                            //     border: "none",
                            //     position: "relative",
                            // }}
                        >
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                                    Sending...
                                </>
                            ) : offeringType === "E-Book" ? (
                                "Send Me The E-Book"
                            ) : (
                                "Get Early Access"
                            )}
                        </button>
                    </div>
                    <p className="text-center text-muted small mt-3 mb-0">
                        No spam. Only updates and early access invitations.
                    </p>
                </>
            )}
        </Box>
    </Modal>
);

const ManufacturingERPLeadMagnet = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        nicheType:"",
        industry_other: "",
        company_name : "",
    });
    const [showError, setShowError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [downloadLink, setDownloadLink] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const { slug: paramSlug } = useParams();
    const location = useLocation();

    const nicheTypes = [
        "Manufacturing & Industrial",
        "Technology & Software",
        "Professional Services",
        "Retail & E-commerce",
        "Construction & Real Estate",
        "Logistics & Supply Chain",
        "Energy & Utilities",
        "Healthcare & Life Sciences",
        "Education & Nonprofit",
        "Finance & Insurance",
        "Media, Entertainment & Hospitality",
        "Other",
    ];

    const [slug, setSlug] = useState(() => {
        const path = window.location.pathname;
        const match = path.match(/\/lm\/(.+)/);
        return match ? match[1] : "";
    });

    const fallbackSlugMap = {
        "/lm/manufacturing-erp": "manufacturing-erp",
    };

    // final slug = either paramSlug OR fallback
    const actualSlug = paramSlug || fallbackSlugMap[location.pathname] || "";

    // get UTM params from query string
    const searchParams = new URLSearchParams(location.search);
    const utmParams = {
        utm_source: searchParams.get("utm_source") || "direct",
        utm_medium: searchParams.get("utm_medium") || "email",
        utm_campaign: searchParams.get("utm_campaign") || "default",
    };


    const {
        apiData: leadMagnet,
        isLoading: isLeadMagnetLoding,
        error: leadMagnetError,
    } = useFetch(() => getLeadMagnet(actualSlug, utmParams), actualSlug);

    // const {
    //   apiData: leadMagnet,
    //   isLoading: isLeadMagnetLoding,
    //   error: leadMagnetError,
    // } = useFetch(getLeadMagnet, slug);

    useEffect(() => {
        if (leadMagnetError) {
            cogoToast.error("Failed to load content");
        }
        if (leadMagnet?.status === "error") {
            cogoToast.error("Failed to load content");
        }
    }, [leadMagnetError, leadMagnet]);

    useEffect(() => {
        document.title = "Manufacturing ERP - Plural Code Technologies";
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const openModal = () => setIsModalOpen(true);

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({
            name: "",
            email: "",
            phone: "",
            company_name: "",
            //   nicheType: "",
            //   industry_other: "",
        });
        setShowError(false);
    };

    const submitLeadForm = async () => {
        const { name, email, phone, nicheType, industry_other, company_name } = formData;

        if (!name.trim() || !email.trim() || !phone.trim() || !company_name.trim()) {
            setShowError(true);
            cogoToast.info("Please fill in all required fields");
            return;
        }

        if (nicheType === "Other" && !industry_other.trim()) {
            setShowError(true);
            cogoToast.info("Please specify your industry");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setShowError(true);
            cogoToast.error("Please enter a valid email address");
            return;
        }

        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(phone)) {
            setShowError(true);
            cogoToast.error("Phone number must be exactly 10 digits");
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                name,
                email,
                phone,
                slug: leadMagnet?.slug,
                company_name,
                offering_type: leadMagnet?.offering_type,
            };

            const response = await postLeadMagnetResponseForm(payload);

            if (response.status === "ok") {
                if (
                    leadMagnet?.offering_type === "E-Book" &&
                    response.data?.download_token
                ) {
                    setDownloadLink(response.data.download_token);
                    setFormData({
                        name: "",
                        email: "",
                        phone: "",
                        company_name: "",
                       
                    });
                    setShowError(false);
                    setIsFormSubmitted(true);
                    cogoToast.success(
                        "Thank you! Your download link is now available below."
                    );
                } else if (leadMagnet?.offering_type === "Manufacturing ERP") {
                    cogoToast.success(
                        "Manufacturing ERP registration successful! Check your email for details."
                    );
                    setFormData({
                        name: "",
                        email: "",
                        phone: "",
                        company_name: "",

                    });
                    setShowError(false);
                    setIsFormSubmitted(true);
                    closeModal();
                }
            } else {
                cogoToast.error(
                    response.message || "Something went wrong. Please try again."
                );
            }
        } catch (err) {
            console.error("Form submission error:", err);
            cogoToast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleManualDownload = async () => {
        if (!downloadLink) {
            cogoToast.error("No download token available");
            return;
        }

        try {
            const downloadResult = await downloadLeadMagnet(slug, downloadLink);

            if (downloadResult.success) {
                // Create download link and trigger download
                const link = document.createElement("a");
                link.href = downloadResult.fileUrl;
                link.download = downloadResult.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                cogoToast.success("Download started! Check your Downloads folder.");

                // Clear the download link since it's one-time use
                setDownloadLink("");

                // Clean up the blob URL after some time
                setTimeout(() => {
                    window.URL.revokeObjectURL(downloadResult.fileUrl);
                }, 30000); // 30 seconds
            } else {
                cogoToast.error(
                    downloadResult.error || "Download failed. Please contact our team."
                );
                // Clear the download link if it failed (likely used already)
                setDownloadLink("");
            }
        } catch (error) {
            console.error("Download error:", error);
            cogoToast.error("Download failed. Please try again.");
        }
    };

    if (isLeadMagnetLoding) {
        return (
            <div
                className="container text-center"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </div>
        );
    }

    if (leadMagnetError) {
        return (
            <div className="container text-center py-5">
                <h3>Error loading the page</h3>
                <p>{leadMagnetError.message || "Unknown error occurred"}</p>
            </div>
        );
    }

    return (
        <div>
            <Helmet>
                <meta name="title" content={leadMagnet?.meta_title} />
                <meta name="description" content={leadMagnet?.meta_description} />
                <meta
                    property="og:description"
                    content={leadMagnet?.meta_description}
                />
            </Helmet>
            <main className="wrapper">
                <HeroSection
                    content={{ title: leadMagnet?.title, subtitle: leadMagnet?.subtitle }}
                    offeringType={leadMagnet?.offering_type}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showError={showError}
                    isSubmitting={isSubmitting}
                    submitLeadForm={submitLeadForm}
                    downloadLink={downloadLink}
                    handleManualDownload={handleManualDownload}
                    isFormSubmitted={isFormSubmitted}
                    nicheTypes={nicheTypes}
                />
                <DeserveClaritySection
                    content={leadMagnet}
                    isLoading={isLeadMagnetLoding}
                    openModal={openModal}
                />
                <ThingsYouWillGetInThisReport openModal={openModal} />
                <JustWantRightSoftwareSection
                    description={leadMagnet?.description}
                    openModal={openModal}
                />
                <ReportParts
                    badEffects={[
                        {
                            id: 1,
                            icon: images.core1,
                            title: "Production & <br />  Shop Floor Control",
                            text: "Track work orders, BOMs, routings, labor, <br /> and yield, everything that happens on your shop floor.",
                        },
                        {
                            id: 2,
                            icon: images.core2,
                            title:
                                "Inventory &  <br /> Supply Chain",
                            text: "Plan material needs with MRP, <br /> manage lots/serials, and track stock from raw material to dispatch.",
                        },
                        {
                            id: 3,
                            icon: images.core4,
                            title: "Quality  <br /> Management",
                            text: "Catch defects early with NCR & CAPA,<br />  monitor inspection reports, and maintain compliance across batches.",
                        },
                        {
                            id: 4,
                            icon: images.core3,
                            title: "Financial <br />  Management",
                            text: "Integrated costing, <br />  ledger, payables, receivables, and real-time job profitability.",
                        },
                        {
                            id: 5,
                            icon: images.core5,
                            title: "Reporting & <br />  Analytics",
                            text: "Live dashboards for OEE, production efficiency, <br /> and profitability insights, not just numbers, but decisions.",
                        },
                        {
                            id: 6,
                            icon: images.core6,
                            title: "User Roles & <br />  Permissions",
                            text: "Granular control for every role, <br /> from shop floor operator to plant manager,  ensuring clarity and accountability.",
                        },
                    ]}
                    helpOffer={leadMagnet?.help_offer}
                    offeringType={leadMagnet?.offering_type}
                    openModal={openModal}
                />
                {/* <ThingsYouWillGetInThisReport openModal={openModal} /> */}
                {/* <WhoIsThisFor openModal={openModal} /> */}
                {/* <HowItWorks openModal={openModal} /> */}

                {/* <TestimonialsSection
                    reviews={leadMagnet?.reviews || []}
                    // reviews={[
                    //   {
                    //     id: 1,
                    //     name: "Pooja",
                    //     designation: "Founder, Trendora Online Retail Pvt. Ltd.",
                    //     content:
                    //       "I almost hired a team that was charging me 3x the fair price. Their pitch sounded perfect, but I didn't even know what they were offering. This report helped me question their proposal,  and I saved over ₹2.5 lakhs just like that.",
                    //     image: images.img4,
                    //   },
                    //   {
                    //     id: 2,
                    //     name: "Rohit",
                    //     designation: "Owner, CloudScribe Technologies Inc.",
                    //     content:
                    //       "Agencies used to throw terms at me,  tech stack, cloud infra, APIs,  and I'd just nod. After this audit, I actually understood how software is built. I could ask the right questions and for the first time, I felt like I was leading the project.",
                    //     image: images.img1,
                    //   },
                    //   {
                    //     id: 3,
                    //     name: "Naveen",
                    //     designation:
                    //       "Co Founder, SwiftHaul Logistics Solutions Pvt. Ltd.",
                    //     content:
                    //       "One team quoted me 1 lakh, another quoted 9. I was so confused. The report broke things down clearly,  now I knew what to expect, and what to reject.",
                    //     image: images.img3,
                    //   },
                    //   {
                    //     id: 4,
                    //     name: "Dr. Anjali",
                    //     designation: "CEO, Medcura Health Services LLP",
                    //     content:
                    //       "We wanted to digitize our patient records and appointment system, but every developer gave us a different quote and plan. Honestly, I had no idea what was necessary and what was just 'nice to have'. This report showed me what a proper healthcare system should actually include,  now we're building it step-by-step, without wasting money or time.",
                    //     image: images.img2,
                    //   },
                    // ]}
                    offeringType={leadMagnet?.offering_type}
                /> */}
                {/* <WhatYouWillGet
                    badEffects={[
                        {
                            id: 1,
                            icon: images.bulb,
                            title: "Understand your <br /> project technically",
                            text: "No more nodding to jargon <br />  or confusing tech buzzwords.",
                        },
                        {
                            id: 2,
                            icon: images.brain,
                            title: "Know the right <br /> tech stack",
                            text: "That fits your business,  <br /> not some cheap or overkill setup.",
                        },
                        {
                            id: 3,
                            icon: images.money,
                            title: "Avoid <br /> overpaying",
                            text: "For features or technology <br /> you don't even need.",
                        },
                        {
                            id: 4,
                            icon: images.tools,
                            title: "Spot <br /> red flags",
                            text: "In agency proposals that fool <br /> you before it’s too late.",
                        },
                        {
                            id: 5,
                            icon: images.target,
                            title: "Define <br /> a clear MVP",
                            text: "So you don't build the Taj Mahal  <br />  when all you need is a cottage.",
                        },
                        {
                            id: 6,
                            icon: images.graph,
                            title: "Plan for <br /> future scalability",
                            text: "So your tech doesn’t collapse <br /> the moment you start to grow.",
                        },
                        {
                            id: 7,
                            icon: images.discuss,
                            title: "Talk confidently <br /> to developers",
                            text: "You’ll finally understand and <br />  speak their tech language.",
                        },
                        {
                            id: 8,
                            icon: images.control,
                            title: "Stay <br /> in control",
                            text: "Of your budget, your timeline,<br /> and product quality expectations.",
                        },
                    ]}
                    helpOffer={leadMagnet?.help_offer}
                    offeringType={leadMagnet?.offering_type}
                    openModal={openModal}
                /> */}
                <FinalCtaSection
                    offeringType={leadMagnet?.offering_type}
                    openModal={openModal}
                />
                {/* <MiniStory openModal={openModal} /> */}

                <LeadCaptureModal
                    isOpen={isModalOpen}
                    closeModal={closeModal}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showError={showError}
                    isSubmitting={isSubmitting}
                    submitLeadForm={submitLeadForm}
                    downloadLink={downloadLink}
                    handleManualDownload={handleManualDownload}
                    isFormSubmitted={isFormSubmitted}
                    offeringType={leadMagnet?.offering_type}
                    nicheTypes={nicheTypes}
                />

                {/* <Faq leadMagnet={leadMagnet}
                    offeringType={leadMagnet?.offering_type}
                /> */}

            </main>
        </div>
    );
};

export default ManufacturingERPLeadMagnet;
